import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Welcome splash — TikTok-style intro shown once per session when a sub-admin
 * (or the master, if configured for himself) enters the admin panel.
 * • Circular profile photo pulses at the top
 * • Configured welcome text appears with a stagger
 * • Configured audio auto-plays (browser autoplay-with-audio requires
 *   a user gesture — we fall back to a "tap to hear" button if muted)
 * • Slow-motion floating stars decorate the background
 * • Auto-dismisses when audio ends (or after 5s if no audio)
 * Requested Feb 27, 2026.
 */
const WelcomeSplash = ({ user, onDone }) => {
  const audioRef = useRef(null);
  const [needsTap, setNeedsTap] = useState(false);
  // Phased exit — first the text fades, then avatar, then whole overlay.
  // Owner asked Feb 27, 2026 for a graceful outro instead of hard cut.
  const [phase, setPhase] = useState("intro"); // intro | outro-text | outro-avatar | done
  const hasAudio = !!user?.welcome_audio;
  const welcomeText = user?.welcome_text || "مرحباً بك في تطبيق جملتي";

  useEffect(() => {
    let done = false;
    // Kick off the phased exit ~4.2s in, so the full outro finishes by 5s.
    const startOutro = setTimeout(() => {
      setPhase("outro-text");
      setTimeout(() => setPhase("outro-avatar"), 350);
      setTimeout(() => { if (!done) { done = true; onDone?.(); } }, 900);
    }, 4100);

    if (hasAudio) {
      const el = audioRef.current;
      if (el) {
        const playPromise = el.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => setNeedsTap(true));
        }
      }
    }
    return () => { clearTimeout(startOutro); done = true; };
  }, [hasAudio, onDone]);

  const handleTapPlay = () => {
    setNeedsTap(false);
    audioRef.current?.play();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        data-testid="welcome-splash"
        className="fixed inset-0 z-[500] bg-gradient-to-br from-[#0F172A] via-[#1E3A5F] to-[#0EA5E9] overflow-hidden flex flex-col items-center justify-center"
        dir="rtl"
      >
        {/* Floating slow-motion stars (background) */}
        {Array.from({ length: 14 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute pointer-events-none text-[#FBBF24]"
            initial={{
              x: `${Math.random() * 100}%`,
              y: `${Math.random() * 100}%`,
              opacity: 0,
              scale: 0.4 + Math.random() * 0.8,
            }}
            animate={{
              y: [`${Math.random() * 100}%`, `${Math.random() * 100}%`, `${Math.random() * 100}%`],
              opacity: [0, 0.8, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 6 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut",
            }}
          >
            <Star className="w-6 h-6 fill-current" />
          </motion.div>
        ))}

        {/* No skip button — the splash always plays for 5 seconds
            (owner's request Feb 27, 2026). */}

        {/* Profile photo (pulsing circle) — larger per owner's request */}
        <motion.div
          initial={{ scale: 0, rotate: -30, opacity: 0 }}
          animate={
            phase === "outro-avatar" || phase === "done"
              ? { scale: 0.3, opacity: 0, y: -40 }
              : { scale: 1, rotate: 0, opacity: 1 }
          }
          transition={{ type: "spring", damping: 12, stiffness: 100, delay: 0.2 }}
          className="relative mb-8"
        >
          {/* Outer glow rings */}
          <motion.div
            className="absolute inset-0 rounded-full bg-gradient-to-br from-[#F5A623] to-[#E85D45]"
            animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute inset-0 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#22C55E]"
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.1, 0.5] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
          />
          {/* Actual avatar — larger */}
          <div className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-full bg-gradient-to-br from-[#F5A623] to-[#E85D45] text-white flex items-center justify-center font-black text-8xl overflow-hidden shadow-2xl border-4 border-white z-10">
            {user?.profile_photo ? (
              <img
                src={`${API}/files/${user.profile_photo}`}
                alt={user?.full_name || ""}
                className="w-full h-full object-cover"
              />
            ) : (
              (user?.full_name || "?")[0]
            )}
          </div>
          {/* Sparkles decoration */}
          <motion.div
            className="absolute -top-4 -right-4 text-[#FBBF24] z-20"
            animate={{ rotate: [0, 20, -20, 0], scale: [1, 1.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles className="w-10 h-10 fill-[#FBBF24]" />
          </motion.div>
        </motion.div>

        {/* Name — larger */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={
            phase.startsWith("outro")
              ? { opacity: 0, y: -20, filter: "blur(6px)" }
              : { opacity: 1, y: 0, filter: "blur(0px)" }
          }
          transition={{ delay: phase === "intro" ? 0.5 : 0, duration: 0.4 }}
          className="text-white font-black text-2xl mb-3"
        >
          {user?.full_name || ""}
        </motion.p>

        {/* Welcome text — larger, fades out first */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={
            phase.startsWith("outro")
              ? { opacity: 0, y: -30, scale: 0.85, filter: "blur(8px)" }
              : { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
          }
          transition={{ delay: phase === "intro" ? 0.7 : 0, duration: 0.5 }}
          data-testid="welcome-splash-text"
          className="text-white font-black text-3xl sm:text-5xl text-center px-6 leading-tight drop-shadow-lg max-w-2xl"
        >
          {welcomeText}
        </motion.h1>

        {/* Tap-to-play (only if autoplay blocked) */}
        {needsTap && (
          <motion.button
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={handleTapPlay}
            data-testid="welcome-splash-tap-play"
            className="mt-6 bg-white text-[#1E3A5F] font-black text-sm px-6 py-3 rounded-full shadow-2xl flex items-center gap-2 hover:bg-white/90 transition-colors animate-pulse"
          >
            🔊 اضغط لسماع الترحيب
          </motion.button>
        )}

        {/* Audio element */}
        {hasAudio && (
          <audio
            ref={audioRef}
            src={`${API}/files/${user.welcome_audio}`}
            preload="auto"
          />
        )}

        {/* Bottom brand */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-6 text-white/70 text-xs font-bold tracking-wider"
        >
          جملتي · الإدارة
        </motion.p>
      </motion.div>
    </AnimatePresence>
  );
};

export default WelcomeSplash;
